import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Outlet, useNavigate } from "react-router-dom";
import { TourProvider } from "@reactour/tour";
import Sidebar from "@/features/admin-layout/components/Sidebar";
import { useAppSelector, useResponsive } from "@/hooks";
import { useFetchUserOrganizationsQuery } from "@/features/organizations/api/organizationsApi";
import { useFetchAccessQuery } from "@/api/accessApi";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import Scrollbars from "react-custom-scrollbars-2";
import MobileNavigation from "./components/MobileNavigation";
import NewClientFormDrawer from "@/components/NewClientFormDrawer";
import CopilotFAB from "@/features/copilot/components/CopilotFAB";

const tourSteps = [
  {
    selector: '[data-tour="organizations-list"]',
    content: "Navega entre tus comercios. *Próximamente",
    position: "right",
  },
  {
    selector: '[data-tour="organization-details"]',
    content: "Actualiza la información de tu restaurante.",
    position: "right",
  },
  {
    selector: '[data-tour="organization-menu"]',
    content: "Actualiza los detalles de tu menú. Estado, servicios, descripción y más...",
    position: "right",
  },
  {
    selector: '[data-tour="organization-content"]',
    content: "Crea y actualiza el contenido de tus menús.",
    position: "right",
  },
  {
    selector: '[data-tour="organization-settings"]',
    content: "Configura la app según tus preferencias.",
    position: "right",
  },
  {
    selector: '[data-tour="user-menu"]',
    content: "Cierra sesión ó cambia tu contraseña. Próximamente: perfil de usuario",
    position: "right",
  },
];

const AdminLayout = () => {
  const { isMobile } = useResponsive();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const uid = useAppSelector((state) => state?.auth?.user?.uid);
  const userAccess = useFetchAccessQuery(uid ?? skipToken);
  const navigate = useNavigate();
  const isSuperAdmin = useAppSelector((state) => state?.auth?.user?.role === "super-admin");

  useFetchUserOrganizationsQuery(userAccess?.data ?? skipToken, {
    skip: Boolean(!userAccess?.data?.length),
  });

  useEffect(() => {
    setIsSidebarExpanded(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (userAccess?.error?.status === 404) {
      navigate("/onboarding", { replace: true });
    }
  }, [navigate, userAccess?.error?.status]);

  return (
    <TourProvider steps={tourSteps}>
      {isSuperAdmin && <CopilotFAB />}
      {isMobile ? <MobileNavigation isSidebarExpanded={isSidebarExpanded} setIsSidebarExpanded={setIsSidebarExpanded} /> : null}
      <Box sx={{ display: 'flex', height: "100vh" }}>
        {isMobile ? (
          <Sidebar isSidebarExpanded={isSidebarExpanded} setIsSidebarExpanded={setIsSidebarExpanded} />
        ) : (
          <Box
            sx={{
              width: isSidebarExpanded ? 300 : 100,
              transition: "width 0.2s",
              flexShrink: 0,
            }}
          >
            <Sidebar isSidebarExpanded={isSidebarExpanded} setIsSidebarExpanded={setIsSidebarExpanded} />
          </Box>
        )}
        <Box
          sx={{
            flexGrow: 1,
            height: "100%",
            paddingTop: 2,
            paddingBottom: 2,
            paddingRight: 2,
            boxSizing: 'border-box',
            transition: "all 0.2s",
          }}
        >
          <Box
            sx={{
              borderRadius: 1.5,
              backgroundColor: "#DDEAFD",
              height: "100%",
              overflowY: "scroll",
              scrollbarWidth: "none",
            }}
          >
            <Scrollbars
              autoHide
              style={{ flex: 1 }}
              renderView={(props) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    overflowY: "scroll",
                    overflowX: "hidden",
                  }}
                />
              )}
            >
              <Outlet />
            </Scrollbars>
          </Box>
        </Box>
      </Box>
      <NewClientFormDrawer />
    </TourProvider>
  );
};

export default AdminLayout;
